import { useEffect, useMemo, useState } from "react";
import { Link, useLoaderData, useNavigate, useParams } from "react-router-dom";
import {
  ParkerInfo,
  ParkerSubscription,
  SubListEntry,
  Vehicle,
} from "./Data/ApiTypes";
import { CloseButton, Col, Container, Row, Card, Form, Badge } from "react-bootstrap";
import { Hyperlink } from "./Controls/Hyperlink";
import { Api } from "./Data/Api";
import { processResult } from "./Data/Result";
import { toast } from "react-toastify";
import { ConfirmModal } from "./Controls/ConfirmModal";
import {
  InviteParkerForm,
  InviteParkerModal,
} from "./UserPlanDetailsPageInviteParkerModal";
import ParkerEditModal from "./ParkerEditModal";
import { parseParkerInfo } from "./Data/ApiParse";
import { SubscriptionStatus } from "./Data/ApiTransport";
import { ColDef } from "ag-grid-community";
import { gridOneCol } from "./Data/Common";
import { PageBar } from "./Controls/PageBar";
import { DataGrid } from "./Controls/DataGrid";
import { Button } from "./Controls/Button";
import { AddVehicleModal } from "./Controls/AddVehicleModal";
import { ChooseVehicleModal } from "./Controls/ChooseVehicleModal";
import { Trash } from "react-bootstrap-icons";
import { NoMoreSpacesModal } from "./NoMoreSpacesDialog";

export function ParkersPage() {
  const [sub, plans] = useLoaderData() as [
    ParkerSubscription[],
    SubListEntry[]
  ];
  const { facilityId, parkerId } = useParams();
  const [allGuestParkers, setAllGuestParkers] = useState<any[]>([]);
  const [showRemove, setShowRemove] = useState(false);
  const [removeParker, setRemoveParker] = useState<ParkerInfo>();
  const [showInvite, setShowInviteParker] = useState(false);
  const [showParkerEditModal, setShowParkerEditModal] = useState(false);
  const [selectedParker, setSelectedParker] = useState<any>();
  const [showAdd, setShowAdd] = useState(false);
  const [showChoose, setShowChoose] = useState(false);
  const [chooseModalVehicles, setChooseModalVehicles] = useState<Vehicle[]>([]);
  const [chooseModalExclude, setChooseModalExclude] = useState<number[]>([]);
  const [search, setSearch] = useState("");
  const [visibleCount, setVisibleCount] = useState(10);
  const [showNoSpacesModal, setShowNoSpacesModal] = useState(false);
  const [newParkerTargetPlanId, setNewParkerTargetPlanId] = useState<number | null>(null);
  const nav = useNavigate();

  useEffect(() => {
    if (!Array.isArray(sub)) return;

    const guests = sub.flatMap((subscription) => {
      if (!Array.isArray(subscription.parkers)) return [];
      return subscription.parkers.map((guest) => ({
        subscriptionId: subscription.subscriptionId,
        sub: subscription,
        parkerId: guest.parker.parkerId,
        name: guest.parker.name,
        email: guest.parker.emailAddress,
        lpn:
          guest.vehicles.length > 0
            ? guest.vehicles.map((v) => v.licensePlateNumber).join(" | ")
            : "N/A",
        plan: subscription.plan.name,
        planId: subscription.plan.planId,
        isManaged: guest.parker.isManaged,
        quantity: guest.quantity,
        parker: guest.parker,
        vehicles: guest.vehicles,
        facilityId: subscription.plan.facilityId
      }));
    });

    setAllGuestParkers(guests);
  }, [sub]);

  async function vehicleAdd(data: Vehicle) {
    if (selectedParker) {
      data.parkerId = selectedParker.parker.parkerId;
    }

    const addRes = await Api.vehicleAdd(data);

    if (!addRes.isOk) {
      toast.error(addRes.error);
      return;
    }

    setAllGuestParkers((prevParkers) => {
      const targetParker = allGuestParkers.find(
        (sp) => sp.parker.parkerId === addRes.value.parkerId
      );

      if (!targetParker) {
        return prevParkers;
      }

      const updated = prevParkers.map((p) => {
        if (p.parker.parkerId === targetParker.parker.parkerId) {
          return {
            ...p,
            vehicles: [...p.vehicles, addRes.value],
          };
        }
        return p;
      });

      const newSelected = updated.find(
        (p) => p.parker.parkerId === targetParker.parker.parkerId
      );
      setSelectedParker(newSelected);

      return updated;
    });

    await vehicleLink(addRes.value, false);
  }

  async function vehicleLink(vehicle: Vehicle, needsUpdate: boolean = true) {
    const addToSub = await Api.subscriptionVehicleAdd(
      selectedParker.subscriptionId,
      {
        vehicleId: vehicle.vehicleId,
        parkerId: selectedParker?.parker.parkerId,
      }
    );
    if (!addToSub.isOk) {
      toast.error(addToSub.error);
      return;
    }

    if (needsUpdate) {
      setAllGuestParkers((prevParkers) => {
        const targetParker = allGuestParkers.find(
          (sp) => sp.parker.parkerId === vehicle.parkerId
        );

        if (!targetParker) {
          return prevParkers;
        }

        const updated = prevParkers.map((p) => {
          if (p.parker.parkerId === targetParker.parker.parkerId) {
            return {
              ...p,
              vehicles: [...p.vehicles, vehicle],
            };
          }
          return p;
        });

        const newSelected = updated.find(
          (p) => p.parker.parkerId === targetParker.parker.parkerId
        );
        setSelectedParker(newSelected);

        return updated;
      });
    }

    toast.success("Vehicle Added Successfully");
  }

  async function vehicleRemove(vehicle: Vehicle) {
    const deleteRes = await Api.subscriptionVehicleDelete(
      selectedParker.subscriptionId,
      vehicle.vehicleId,
      {
        vehicleId: vehicle.vehicleId,
        parkerId: selectedParker ? selectedParker.parker.parkerId : undefined,
      }
    );

    if (!deleteRes.isOk) {
      toast.error(deleteRes.error as string);
      return;
    }

    setAllGuestParkers((prevParkers) => {
      const updated = prevParkers.map((p) => {
        const existsInSub = allGuestParkers.some(
          (sp) => sp.parker.parkerId === p.parker.parkerId
        );

        if (existsInSub) {
          return {
            ...p,
            vehicles: p.vehicles.filter(
              (v: any) => v.vehicleId !== vehicle.vehicleId
            ),
          };
        }

        return p;
      });

      const newSelected = updated.find(
        (p) => p.parker.parkerId === selectedParker?.parker.parkerId
      );
      setSelectedParker(newSelected);

      return updated;
    });
  }

  const openChooseModalForParkersList = async () => {
    const result = await Api.vehicleListByParker(
      selectedParker!.parker.parkerId
    );

    if (result.isOk) {
      setChooseModalVehicles(result.value);
    } else {
      setChooseModalVehicles([]);
    }
    setChooseModalExclude(
      selectedParker!.vehicles.map((x: any) => x.vehicleId)
    );
    setShowChoose(true);
  };

  const activePlans = plans.filter(
    (x) => x.status == SubscriptionStatus.Active
  );

  const filterBySearch = (list: any[]) => {
    if (list.length === 0) return list;
    
    return list.filter((p) => 
      p.name?.toLowerCase().includes(search.toLowerCase()) ||
      p.email?.toLowerCase().includes(search.toLowerCase()) ||
      p.lpn?.toLowerCase().includes(search.toLowerCase()) ||
      p.plan?.toLowerCase().includes(search.toLowerCase())
    );
  };

  const filteredParkers = search && allGuestParkers.length > 0 
    ? filterBySearch(allGuestParkers) 
    : allGuestParkers;
  const shownParkers = filteredParkers.slice(0, visibleCount);

  function parkerInvite(data: InviteParkerForm) {
    const targetPlan = activePlans.find((x) => x.plan.planId === data.planId);
    setNewParkerTargetPlanId(targetPlan!.subscriptionId);

    Api.subParkerInvite(targetPlan!.subscriptionId, {
      emailAddress: data.emailAddress,
      name: data.name,
      quantity: 1,
      subscriptionId: targetPlan!.subscriptionId,
      isLoginEnabled: data.isLoginEnabled,
    }).then((resp) =>
      processResult(
        resp,
        (val) => {
          toast.success("Parker Added Successfully");
          setAllGuestParkers((prev) => [
            ...prev,
            {
              subscriptionId: targetPlan!.subscriptionId,
              parkerId: val.parker.parkerId,
              name: val.parker.name,
              lpn: "N/A",
              plan: targetPlan!.plan.name,
              email: val.parker.emailAddress,
              quantity: val.quantity,
              isManaged: val.parker.isManaged,
              parker: parseParkerInfo(val.parker),
              vehicles: [],
              sub: {
                subscriptionId: targetPlan!.subscriptionId,
                status: SubscriptionStatus.Active,
              },
            },
          ]);
        },
        (str) => {
          if (str?.toLowerCase().includes("spots available=0")) {
            setShowInviteParker(false);
            setShowNoSpacesModal(true);
          } else {
            toast.error(str);
          }
        }
      )
    );
  }

  function parkerRemove(parker: ParkerInfo): void {
    const target = allGuestParkers.find((x) => x.parkerId === parker.parkerId);
    Api.subParkerRemove(target.subscriptionId, {
      parkerId: parker.parkerId,
    }).then((resp) =>
      processResult(
        resp,
        (val) => {
          toast.success("Parker Removed Successfully");
          const updated = allGuestParkers.filter((p) =>
            p.parker.parkerId !== parker.parkerId ? true : false
          );
          setAllGuestParkers([...updated]);
        },
        (err) => alert(err)
      )
    );
  }

  const columns = useMemo<ColDef[]>(
    () => [
      {
        field: "name",
        headerName: "Name",
        sortable: true,
        filter: true,
        flex: 1,
        minWidth: 200,
        cellRenderer: (params: any) => {
          const name = params.value;

          return (
            <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <Hyperlink
                onClick={() => {
                  setSelectedParker(params.data);
                  setShowParkerEditModal(true);
                }}
              >
                {name}
              </Hyperlink>
            </span>
          );
        },
      },
      {
        field: "managedBy",
        headerName: "Managed By",
        sortable: true,
        filter: true,
        flex: 1,
        minWidth: 80,
        valueGetter: (params: any) => {
          return params.data.isManaged ? "Self" : "Group";
        },
        cellRenderer: (params: any) => {
          const isManaged = params.data.isManaged;
          return (
            <span
              className={`badge ${isManaged ? "bg-primary" : "bg-secondary"}`}
              style={{ whiteSpace: "nowrap" }}
            >
              {isManaged ? "Self" : "Group"}
            </span>
          );
        },
      },
      
      {
        field: "lpn",
        headerName: "LPN",
        flex: 1,
        minWidth: 120,
        valueGetter: (params) => {
          const vehicles = params.data.vehicles || [];
          return vehicles.length > 0
            ? vehicles.map((v: any) => v.licensePlateNumber).join(" | ")
            : "N/A";
        },
      },

      {
        field: "plan",
        headerName: "Plan",
        sortable: true,
        filter: true,
        flex: 1,
        minWidth: 130,
        cellRenderer: (params: any) => {
          const plan = params.value;
          const subscriptionId = params.data.subscriptionId;
          const url = `/user/plan/${subscriptionId}`;

          return (
            <a
              onClick={(e) => {
                e.preventDefault();
                nav(url, {
                  state: {
                    from: "parkersList",
                    parentParkerId: parkerId,
                  },
                });
              }}
              href={url}
              style={{
                color: "#d60087",
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              {plan}
            </a>
          );
        },
      },
      {
        field: "",
        headerName: "",
        sortable: false,
        filter: false,
        flex: 0,
        width: 60,
        cellRenderer: (params: any) => {
          return (
            <CloseButton
              onClick={() => {
                setRemoveParker(params.data.parker);
                setShowRemove(true);
              }}
              className="cursor-pointer"
            />
          );
        },
      },
    ],
    [facilityId, parkerId, nav]
  );

  return (
    <Container fluid className="h-100">
      <Row className="h-100">
        <Col style={{ ...gridOneCol("max-content 1fr 5px"), display: "flex", flexDirection: "column", height: "90vh" }}>
          <div className="d-flex justify-content-between align-items-center mt-3 mb-2">
            <PageBar title="Parkers" />
            {activePlans.length > 0 && (
              <Button
                className="btn-main"
                onClick={() => setShowInviteParker(true)}
              >
                Add Parker
              </Button>
            )}
          </div>
          
          {/* Desktop View */}
          <div className="d-none d-md-block" style={{ flex: 1, minHeight: 200, height: "100%" }}>
            <DataGrid
              rowData={allGuestParkers.sort((a, b) =>
                a.name.localeCompare(b.name)
              )}
              columnDefs={columns}
            />
          </div>

          {/* Mobile View */}
          <div className="d-block d-md-none" style={{ flex: 1, overflowY: "auto" }}>
            <Form.Control
              type="search"
              placeholder="Search parkers..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setVisibleCount(10);
              }}
              className="mb-3 sticky-top bg-white"
            />

            {shownParkers.length === 0 ? (
              <div className="text-center text-muted py-3">
                No Rows to Show
              </div>
            ) : (
              shownParkers.sort((a, b) => a.name.localeCompare(b.name)).map((parker, idx) => (
                <Card
                  key={idx}
                  className="mb-3 rounded-4 border-1"
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    setSelectedParker(parker);
                    setShowParkerEditModal(true);
                  }}
                >
                  <Card.Body className="p-3">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div className="flex-grow-1">
                        <h6 className="mb-1 fw-bold">{parker.name}</h6>
                        <div className="text-muted small">{parker.email}</div>
                      </div>
                      <button
                        className="btn btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setRemoveParker(parker.parker);
                          setShowRemove(true);
                        }}
                        style={{ marginLeft: "8px" }}
                      >
                        <Trash className="icon-shift-up" size={16} />
                      </button>
                    </div>

                    <div className="row g-2">
                      <div className="col-6">
                        <div className="bg-light rounded-3 py-2 px-2">
                          <div className="text-muted small">PLAN</div>
                          <div 
                            className="fw-bold fs-6"
                            onClick={(e) => {
                              e.stopPropagation();
                              const url = `/user/plan/${parker.subscriptionId}`;
                              nav(url, {
                                state: {
                                  from: "parkersList",
                                  parentParkerId: parkerId,
                                },
                              });
                            }}
                            style={{
                              color: "#d60087",
                              textDecoration: "underline",
                              cursor: "pointer"
                            }}
                          >
                            {parker.plan}
                          </div>
                        </div>
                      </div>

                      <div className="col-6">
                        <div className="bg-light rounded-3 py-2 px-2">
                          <div className="text-muted small">MANAGED BY</div>
                          <div className="fw-bold fs-6">
                            <Badge 
                              bg={parker.isManaged ? "primary" : "secondary"}
                              className="fs-6"
                            >
                              {parker.isManaged ? "Self" : "Group"}
                            </Badge>
                          </div>
                        </div>
                      </div>

                      <div className="col-12">
                        <div className="bg-light rounded-3 py-2 px-2">
                          <div className="text-muted small">LICENSE PLATES</div>
                          <div className="fw-bold fs-6">{parker.lpn}</div>
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              ))
            )}

            {visibleCount < filteredParkers.length && shownParkers.length > 0 && (
              <div className="text-center mb-3">
                <Button
                  className="btn-main"
                  onClick={() => setVisibleCount((v) => v + 10)}
                >
                  Load More
                </Button>
              </div>
            )}
          </div>
        </Col>
      </Row>

      <AddVehicleModal
        show={showAdd}
        setShow={setShowAdd}
        onSubmit={vehicleAdd}
      />

      <ChooseVehicleModal
        show={showChoose}
        setShow={setShowChoose}
        onSubmit={vehicleLink}
        vehicles={chooseModalVehicles}
        exclude={chooseModalExclude}
        onClose={() => setShowChoose(false)}
      />

      <InviteParkerModal
        show={showInvite}
        setShow={setShowInviteParker}
        submit={parkerInvite}
        plans={activePlans.map((x) => x.plan)}
      />
      <ConfirmModal
        title="Remove Parker"
        message="Are you sure you want to remove this parker from your plan?"
        show={showRemove}
        setShow={setShowRemove}
        onConfirm={() => {
          if (removeParker !== undefined) {
            parkerRemove(removeParker);
          }
        }}
      />

      {selectedParker && (
        <ParkerEditModal
          show={showParkerEditModal}
          onClose={() => {
            setShowParkerEditModal(false);
            setSelectedParker(undefined);
          }}
          parker={selectedParker}
          setParker={setSelectedParker}
          subStatus={selectedParker.sub.status}
          onAddNew={() => setShowAdd(true)}
          onAddExisting={() => openChooseModalForParkersList()}
          onRemoveVehicle={vehicleRemove}
          subId={selectedParker.subscriptionId}
          parkers={allGuestParkers}
          setParkers={setAllGuestParkers}
          initialWaitlistRequestPayload={{
            parkerId: selectedParker.parkerId,
            facilityId: selectedParker.facilityId,
            planEntryId: selectedParker.sub.planEntryId,
            spacesRequested: selectedParker.quantity,
            isAddingToExistingParker: true,
            subscriptionId: selectedParker.subscriptionId
         }}
          isWaitlistEnabled={plans.find(x => x.plan.planId === selectedParker.planId)?.facility.isWaitlistEnabled!}
          currentSpaces={selectedParker.quantity}
          hasWaitlistEntries={plans.find(x => x.plan.planId === selectedParker.planId)?.facility.hasWaitlistEntries!}
        />
      )}

    <NoMoreSpacesModal
      show={showNoSpacesModal}
      setShow={setShowNoSpacesModal}
      planId={newParkerTargetPlanId!}
    />

    </Container>
  );
}
